import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { LogOut, Gift, Check, Lock } from "lucide-react";
import client from "../lib/api";
import { useAuth } from "../context/AuthContext";

const Rewards = () => {
  const { user, logout } = useAuth();
  const [data, setData] = useState(null);
  const [orders, setOrders] = useState([]);

  useEffect(() => {
    if (!user) return;
    client.get("/rewards").then((r) => setData(r.data)).catch(() => {});
    client.get("/orders").then((r) => setOrders(r.data.orders)).catch(() => {});
  }, [user]);

  if (!user) {
    return (
      <div className="max-w-md mx-auto px-4 pt-28 pb-24 text-center">
        <Gift className="h-14 w-14 text-[#c9ad7f] mx-auto mb-4" />
        <h1 className="font-heading text-2xl font-bold text-[#2c1e12]">Sign in to see your rewards</h1>
        <p className="text-[#8a7458] mt-1">Earn a point for every meal and redeem free treats.</p>
        <Link to="/auth" className="mt-5 inline-flex rounded-xl bg-[#9a4a2f] hover:bg-[#853e26] text-white font-semibold px-6 py-3 transition-colors">Sign In / Sign Up</Link>
      </div>
    );
  }

  const pts = data?.points ?? user.points ?? 0;
  const nextGoal = 20;
  const pct = Math.min(100, (pts / nextGoal) * 100);

  return (
    <div className="max-w-xl mx-auto px-4 pt-20 pb-24">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold text-[#2c1e12]">Rewards</h1>
          <p className="text-[#8a7458]">{user.first_name} {user.last_name}</p>
        </div>
        <button onClick={logout} className="p-2 text-[#8a7458] hover:text-[#9a4a2f]" aria-label="Log out"><LogOut className="h-5 w-5" /></button>
      </div>

      <div className="mt-4 rounded-3xl bg-[#2c1e12] p-6 text-center text-white">
        <p className="text-xs tracking-widest text-[#c9b89f] uppercase">Current Points</p>
        <p className="font-heading text-5xl font-bold mt-1">{pts} <span className="text-2xl text-[#c9b89f]">/ {nextGoal}</span></p>
        <div className="mt-4 h-2 rounded-full bg-white/15 overflow-hidden">
          <div className="h-full bg-[#c0653f]" style={{ width: `${pct}%` }} />
        </div>
        <p className="text-xs text-[#c9b89f] mt-2">{data ? `${data.meals_ordered} meals ordered` : ""}</p>
      </div>

      <h2 className="font-heading text-xl font-bold text-[#2c1e12] mt-6 mb-3">Your Rewards</h2>
      <div className="grid grid-cols-3 gap-3">
        {(data?.tiers || []).map((t) => (
          <div key={t.key} className="rounded-2xl bg-[#f6ece0] p-4 text-center">
            <div className={`mx-auto mb-2 h-9 w-9 rounded-full flex items-center justify-center ${t.available ? "bg-[#2c6e49] text-white" : "bg-[#e6d6b8] text-[#8a7458]"}`}>
              {t.available ? <Check className="h-5 w-5" /> : <Lock className="h-4 w-4" />}
            </div>
            <p className="text-sm font-semibold text-[#2c1e12] leading-tight">{t.label}</p>
            <p className="text-xs text-[#8a7458] mt-0.5">{t.points} pts</p>
            <span className={`mt-2 inline-block text-[11px] font-semibold rounded-full px-2 py-0.5 ${t.available ? "bg-[#d9ecd9] text-[#2c6e49]" : "bg-[#e6d6b8] text-[#8a7458]"}`}>{t.available ? "Available" : "Locked"}</span>
          </div>
        ))}
      </div>
      <p className="text-xs text-[#8a7458] mt-3">Available rewards are applied at checkout as a discount.</p>

      <h2 className="font-heading text-xl font-bold text-[#2c1e12] mt-8 mb-3">Order History</h2>
      {orders.length === 0 ? (
        <p className="text-[#8a7458] text-sm">No orders yet.</p>
      ) : (
        <div className="space-y-2">
          {orders.map((o) => (
            <div key={o.order_number} className="rounded-2xl bg-[#f6ece0] p-4 flex items-center justify-between">
              <div>
                <p className="font-semibold text-[#2c1e12]">{o.order_number}</p>
                <p className="text-xs text-[#8a7458]">{o.fulfillment_date} · {o.fulfillment_type} · {o.items.length} items</p>
              </div>
              <div className="text-right">
                <p className="font-bold text-[#9a4a2f]">${o.total.toFixed(2)}</p>
                <span className="text-xs text-[#8a7458] capitalize">{o.status}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Rewards;
